import { Box, TextField, Typography, Link } from '@mui/material';
import { parseWelcomeDocUrl, buildWelcomeDocPreviewUrl, buildWelcomeDocViewUrl, type WelcomeDocRef } from '../utils/welcomeDocUrl';

interface Props {
    value: WelcomeDocRef;
    onChange: (value: WelcomeDocRef) => void;
    /** Called with the raw input on every change (e.g. so form can persist on submit). */
    onRawChange?: (raw: string) => void;
    label?: string;
    placeholder?: string;
    helperText?: string;
    disabled?: boolean;
}

const DEFAULT_PLACEHOLDER = 'Paste a Google Docs link, e.g. https://docs.google.com/document/d/.../edit?tab=t.xxx';

export default function WelcomeDocInput(props: Props) {
    const { value, onChange, onRawChange, label = 'Welcome document', placeholder = DEFAULT_PLACEHOLDER, helperText, disabled } = props;

    const displayUrl = value.docId ? buildWelcomeDocViewUrl(value) : '';

    const handleInputChange = (raw: string) => {
        const parsed = parseWelcomeDocUrl(raw);
        if (parsed.docId) {
            onChange(parsed);
        } else if (raw.trim() === '') {
            onChange({ docId: null, tabId: null });
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
                fullWidth
                size="small"
                label={label}
                placeholder={placeholder}
                helperText={helperText}
                disabled={disabled}
                defaultValue={displayUrl}
                onChange={e => onRawChange?.(e.target.value)}
                onBlur={e => handleInputChange(e.target.value)}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        (e.target as HTMLInputElement).blur();
                    }
                }}
            />
            {value.docId && (
                <Typography variant="body2" color="text.secondary">
                    Parsed: document ID + {value.tabId ? 'tab' : 'no tab'}.{' '}
                    {displayUrl && (
                        <Link href={displayUrl} target="_blank" rel="noopener noreferrer">
                            Open in Google Docs
                        </Link>
                    )}
                </Typography>
            )}
        </Box>
    );
}

export { buildWelcomeDocPreviewUrl, buildWelcomeDocViewUrl, parseWelcomeDocUrl };
export type { WelcomeDocRef };
